import * as THREE from "three";
import { Cell, Grid } from "@highmarch/domain";
import { Terrain } from "./terrain";
import { NoiseAmp, relief, warp } from "./groundNoise";

/**
 * The ground's geometry (groundMesh.ts): which cells carry ground, the height field with its micro-relief and lateral
 * wander, and the welded surface. The first layer of GroundMesh: groundCliffs.ts, groundMaterials.ts, groundHoles.ts
 * and groundSplat.ts build on it, each a base class of the next, so the one ground keeps one set of state.
 */
export class GroundGeometry {
  /** World units one texture repeat covers (the maps are baked from a 4-unit plane). */
  static readonly TexScale = 4;
  /** Vertical relief of the smooth ground noise, in world units. */
  static readonly Relief = NoiseAmp.Relief;
  /** Quads per cell on the surface. */
  static readonly Sub = 4;
  /** Half-width of the central difference that measures the height field's slope, in world units (a sixteenth of a quad). */
  static readonly SlopeProbe = 0.015;
  /** Lateral wander applied to every ground vertex, so no plateau edge or island rim runs straight along the grid. */
  static readonly Warp = NoiseAmp.Warp;
  /**
   * How far the ground continues past the playable grid, in cells.
   *
   * The world is NOT an island. The design is a continuous world where you can see other players and their battles,
   * so the map has no edge: the ground runs well past the playable rectangle and fades into haze (DESIGN §12's
   * "radial XZ world-space fade for the map edge / fog of war"). An earlier pass made this a floating island with a
   * rim, a rock keel and an ocean, which looked good and was the wrong game.
   */
  static readonly Beyond = 20;
  /** Cells past the playable edge where the haze starts, and where it has fully taken over. */
  // not readonly: the title's diorama (titleDiorama.ts) looks across the land at a low angle and widens the band while it stands
  static FogStart = 4;
  static FogFull = 12;

  constructor(protected readonly grid: Grid, protected readonly terrain: Terrain) {}

  protected readonly land = new Set<string>();
  /** Cells from the nearest non-land cell, so the lateral wander can be strongest exactly at the island's edge. */
  protected readonly edgeDist = new Map<string, number>();
  /** The land mask of the ground currently in the scene, so the scatter views can dress the apron too. */
  static currentLand: Set<string> | null = null;
  /**
   * Ground height at a world point: the terrain's rolling hills plus the shared micro-relief. It is a pure function
   * of the world position and nothing about it is per-cell, which is exactly why the surface has no terraces and no
   * seams — two cells that meet ask the same question at the same point and get the same answer.
   */
  protected heightIn(wx: number, wz: number): number {
    return this.terrain.hillAt(wx, wz) + relief(wx, wz);
  }

  /**
   * Which cells carry ground, over an area larger than the playable grid.
   *
   * The playable area is a rectangle, and an island whose outline IS that rectangle reads as a slab however well the
   * surface is painted. So the land extends up to `Apron` cells past the grid in an irregular fringe driven by noise:
   * the player still plays on the rectangle, but the island they see has headlands, coves and an outline that owes
   * nothing to the grid. Inside the grid the only holes are chasms and lava pools.
   */
  protected buildLand(): void {
    const { grid, terrain } = this;
    const A = GroundGeometry.Beyond;
    this.land.clear();
    for (let z = -A; z < grid.height + A; z++) {
      for (let x = -A; x < grid.width + A; x++) {
        const c = new Cell(x, z);
        // inside the playable rectangle the only holes are chasms and lava; outside it the world simply continues
        if (grid.inBounds(c) && (terrain.isPit(c) || terrain.isLava(c))) continue;
        this.land.add(`${x}|${z}`);
      }
    }
    GroundGeometry.currentLand = this.land;
    // distance in cells from the nearest hole, so the lateral wander is strongest at a chasm's lip
    this.edgeDist.clear();
    let front: [number, number][] = [];
    for (const k of this.land) {
      const [x, z] = k.split("|").map(Number);
      if ([[1, 0], [-1, 0], [0, 1], [0, -1]].some(([dx, dz]) => !this.land.has(`${x + dx}|${z + dz}`))) {
        this.edgeDist.set(k, 0); front.push([x, z]);
      }
    }
    for (let d = 1; d <= 4 && front.length; d++) {
      const next: [number, number][] = [];
      for (const [x, z] of front) {
        for (const [dx, dz] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
          const nk = `${x + dx}|${z + dz}`;
          if (!this.land.has(nk) || this.edgeDist.has(nk)) continue;
          this.edgeDist.set(nk, d); next.push([x + dx, z + dz]);
        }
      }
      front = next;
    }
  }

  protected solid(c: Cell): boolean { return this.land.has(`${c.x}|${c.y}`); }
  /** A hole inside the playable area — a chasm or a lava pool — as opposed to the sea beyond the island's rim. */
  protected isHole(c: Cell): boolean { return this.grid.inBounds(c) && (this.terrain.isPit(c) || this.terrain.isLava(c)); }
  /** Every cell that carries ground, as cells. */
  protected landCells(): Cell[] {
    return [...this.land].map((k) => { const [x, z] = k.split("|").map(Number); return new Cell(x, z); });
  }

  /**
   * How hard to wander a point sideways. Strongest right at a chasm's lip, where a straight run of cells is what
   * gives the grid away, and a gentle drift elsewhere — a large displacement inland would pull the painted road off
   * the line the units walk.
   */
  protected warpAt(wx: number, wz: number): [number, number] {
    const d = this.edgeDist.get(`${Math.round(wx)}|${Math.round(wz)}`) ?? 4;
    // Hard at the lip. A one- or two-cell gorge whose outline is a straight run of cell edges reads as a rectangle
    // cut into the map — the exact rigidity the terrain work was meant to remove.
    //
    // 2.1 and no higher. Pushing it to 5.2 for curvier gorge outlines moved vertices more than a cell, neighbouring
    // ones crossed over, and the surface folded: hard-edged white shards through the cliff faces and chasm floors,
    // where triangles had turned inside out and `computeVertexNormals` handed their zero area back as a zero-length
    // normal. A slightly straighter gorge is worth more than that.
    const k = 1 + 2.1 * (1 - Math.min(1, d / 2.2));
    const [dx, dz] = warp(wx, wz);
    return [dx * k, dz * k];
  }

  protected buildSurface(): THREE.BufferGeometry {
    const { grid, terrain } = this;
    const N = GroundGeometry.Sub;
    const pos: number[] = [], uv: number[] = [], nrm: number[] = [], idx: number[] = [];
    // vertices are keyed by grid position alone: the whole island is one welded sheet with no bands to split it
    const key = new Map<string, number>();
    const push = (gx: number, gz: number, wx: number, wz: number, y: number): number => {
      const k = `${gx}|${gz}`;
      const hit = key.get(k);
      if (hit !== undefined) return hit;
      const i = pos.length / 3;
      const [dwx, dwz] = this.warpAt(wx, wz);
      pos.push(wx + dwx, y, wz + dwz);
      uv.push(wx / GroundGeometry.TexScale, wz / GroundGeometry.TexScale);
      nrm.push(...this.normalIn(wx, wz));
      key.set(k, i);
      return i;
    };
    for (const c of this.landCells()) {
      for (let iz = 0; iz <= N; iz++) {
        for (let ix = 0; ix <= N; ix++) {
          const wx = c.x - 0.5 + ix / N, wz = c.y - 0.5 + iz / N;
          push(c.x * N + ix, c.y * N + iz, wx, wz, this.heightIn(wx, wz));
        }
      }
      for (let iz = 0; iz < N; iz++) {
        for (let ix = 0; ix < N; ix++) {
          const a = key.get(`${c.x * N + ix}|${c.y * N + iz}`)!;
          const b = key.get(`${c.x * N + ix + 1}|${c.y * N + iz}`)!;
          const d = key.get(`${c.x * N + ix}|${c.y * N + iz + 1}`)!;
          const e = key.get(`${c.x * N + ix + 1}|${c.y * N + iz + 1}`)!;
          idx.push(a, d, b, b, d, e);
        }
      }
    }
    void grid; void terrain;
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
    g.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
    g.setAttribute("normal", new THREE.Float32BufferAttribute(nrm, 3));
    g.setIndex(idx);
    return g;
  }

  /**
   * The ground's normal at a world point, taken from the height field itself rather than from the triangles, for the
   * lighting.
   *
   * The triangles are a poor witness to the slope. The lateral wander squeezes and stretches neighbouring quads by
   * different amounts (hardest at a gorge lip, where the gorge levelling also puts its ramps), so a mesh normal is
   * steep on one triangle and flat on the next and the shading picked out the triangulation. The height field is
   * smooth, so this normal is too. (The shader's rock line reads the finer baked slope map, groundSlope.ts.)
   */
  protected normalIn(wx: number, wz: number): [number, number, number] {
    const e = GroundGeometry.SlopeProbe;
    const hx = (this.heightIn(wx + e, wz) - this.heightIn(wx - e, wz)) / (2 * e);
    const hz = (this.heightIn(wx, wz + e) - this.heightIn(wx, wz - e)) / (2 * e);
    const l = Math.hypot(hx, 1, hz);
    return [-hx / l, 1 / l, -hz / l];
  }
}
